import type { Ref, ComputedRef } from 'vue'
import type { Status } from '@injectivelabs/utils'
import type { Address, EIP1193Provider } from 'viem'
import type { Connection, VersionedTransaction } from '@solana/web3.js'

export enum LayerZeroDirection {
  InjectiveEvmToSolana = 'injective-evm-to-solana',
  SolanaToInjectiveEvm = 'solana-to-injective-evm'
}

export enum LayerZeroChainKey {
  InjectiveEvm = 'injectiveevm',
  Solana = 'solana'
}

export enum LayerZeroChainType {
  Evm = 'EVM',
  Solana = 'SOLANA',
  Starknet = 'STARKNET'
}

export enum LayerZeroTransferStatus {
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
  Unknown = 'UNKNOWN'
}

export enum LayerZeroTransferPhase {
  Idle = 'idle',
  Building = 'building',
  Switching = 'switching',
  Signing = 'signing',
  Simulating = 'simulating',
  Confirming = 'confirming',
  Tracking = 'tracking',
  Complete = 'complete',
  Error = 'error'
}

export enum LayerZeroValidationCode {
  NotTransaction = 'notTransaction',
  ChainMismatch = 'chainMismatch',
  SignerMismatch = 'signerMismatch',
  InvalidEncoding = 'invalidEncoding',
  InvalidChainId = 'invalidChainId',
  InvalidTarget = 'invalidTarget',
  InvalidCalldata = 'invalidCalldata',
  InvalidValue = 'invalidValue',
  InvalidTransaction = 'invalidTransaction',
  MetadataUnavailable = 'metadataUnavailable',
  TargetNotAllowed = 'targetNotAllowed'
}

export enum SolanaWalletKind {
  Phantom = 'phantom',
  Solflare = 'solflare'
}

export type LayerZeroQuoteOptions = {
  dstNativeDropAmount?: string
  amountType?: 'EXACT_SRC_AMOUNT'
  feeTolerance?: { amount: number; type: 'PERCENT' }
}

export type LayerZeroEncodedTransaction = {
  to?: string
  data: string
  from?: string
  value?: string
  chainId?: number
  gasLimit?: string
  encoding?: string
}

export type LayerZeroTransactionStep = {
  chainKey: string
  type: 'TRANSACTION'
  description?: string
  signerAddress: string
  chainType: LayerZeroChainType
  transaction: {
    encoded: LayerZeroEncodedTransaction
  }
}

export type LayerZeroRouteStep = {
  type: string
  srcChainKey: string
  description?: string
}

export type LayerZeroFee = {
  type: string
  amount?: string
  chainKey: string
  address?: string
  description?: string
}

export type LayerZeroDuration = {
  estimated?: null | string
}

export type LayerZeroQuote = {
  id: string
  feeUsd?: string
  srcAmount?: string
  dstAmount?: string
  expiresAt?: string
  srcChainKey: string
  dstChainKey: string
  feePercent?: string
  fees?: LayerZeroFee[]
  dstAmountMin?: string
  srcAmountUsd?: string
  dstAmountUsd?: string
  duration?: LayerZeroDuration
  direction: LayerZeroDirection
  routeSteps: LayerZeroRouteStep[]
  userSteps: LayerZeroTransactionStep[]
  options?: { dstNativeDropAmount?: string }
}

export type LayerZeroQuoteRequest = {
  amount: string
  dstWalletAddress: string
  srcWalletAddress: string
  direction: LayerZeroDirection
  options?: LayerZeroQuoteOptions
}

export type LayerZeroChainMetadata = {
  multicall?: string
  transferDelegate?: string
}

export type LayerZeroMetadata = {
  chains: {
    [LayerZeroChainKey.Solana]: LayerZeroChainMetadata
    [LayerZeroChainKey.InjectiveEvm]: LayerZeroChainMetadata
  }
}

export type LayerZeroExecutionHistoryEntry = {
  event: string
  transaction?: { hash: string; chainKey: string; }
}

export type LayerZeroStatusResult = {
  explorerUrl?: string
  status: LayerZeroTransferStatus
  executionHistory?: LayerZeroExecutionHistoryEntry[]
}

export type LayerZeroSession = {
  id: string
  amount: string
  quoteId: string
  srcTxHash: string
  createdAt: number
  updatedAt: number
  explorerUrl?: string
  completedAt?: number
  amountDisplay: string
  srcTxHashes: string[]
  errorMessage?: string
  srcWalletAddress: string
  dstWalletAddress: string
  direction: LayerZeroDirection
  status: LayerZeroTransferStatus
  executionHistory?: LayerZeroExecutionHistoryEntry[]
}

export type LayerZeroStepValidation =
  | { ok: true; step: LayerZeroTransactionStep }
  | { ok: false; code: LayerZeroValidationCode }

export type LayerZeroExecutionPlan =
  | { ok: false; code: LayerZeroValidationCode }
  | { ok: true; steps: LayerZeroTransactionStep[] }

export type LayerZeroExecuteParams = {
  amount: string
  amountDisplay: string
  quote: LayerZeroQuote
  dstWalletAddress: string
  direction: LayerZeroDirection
  solanaAddress?: null | string
  solanaProvider?: null | SolanaProvider
}

export type LayerZeroQuotesRefs = {
  status: Status
  reset: () => void
  isUnavailable: Ref<boolean>
  refresh: () => Promise<void>
  quotes: Ref<LayerZeroQuote[]>
  errorMessage: Ref<null | string>
  bestQuote: ComputedRef<null | LayerZeroQuote>
}

export type LayerZeroBalanceRefs = {
  status: Status
  balance: Ref<bigint>
  refresh: () => Promise<void>
  errorMessage: Ref<null | string>
  formattedBalance: Ref<null | string>
}

export type LayerZeroTransferRefs = {
  status: Status
  reset: () => void
  remove: (id: string) => void
  errorMessage: Ref<null | string>
  resumePendingSessions: () => void
  phase: Ref<LayerZeroTransferPhase>
  sessions: ComputedRef<LayerZeroSession[]>
  resume: (session: LayerZeroSession) => void
  pendingSessions: ComputedRef<LayerZeroSession[]>
  activeSession: ComputedRef<null | LayerZeroSession>
  execute: (params: LayerZeroExecuteParams) => Promise<void>
}

/**
 * Shape exposed by Phantom/Solflare through their injected provider and
 * wrapped by the wallet adapters. Only `signTransaction` is ever used, the
 * destination wallet is never asked to sign.
 */
export type SolanaProvider = {
  isConnected: boolean
  disconnect(): Promise<void>
  publicKey?: null | { toBase58(): string }
  connect(): Promise<{ publicKey: { toBase58(): string } }>
  signTransaction(
    transaction: VersionedTransaction
  ): Promise<VersionedTransaction>
}

export type LayerZeroEvmSigner = {
  address: Address
  provider: EIP1193Provider
}

export type LayerZeroSolanaSigner = {
  address: string
  connection: Connection
  provider: SolanaProvider
}
